<template>
  <!-- PBI 11217: read-only view of one message's feedback in the logs, shaped like the VindFront chat (thumbs and
       thumbs-down panel, PBI 11215) but without controls: nothing here can be changed or sent. The selected state
       is announced as text and shown by more than colour (check mark, weight, border). -->
  <div class="message-feedback-summary">
    <ul class="message-feedback" aria-label="Feedback">
      <li class="feedback-icon" :class="{ active: isThumbsUp }">
        <i class="las la-thumbs-up" aria-hidden="true"></i>
        <span class="feedback-visually-hidden">
          {{ isThumbsUp ? "Helpful, selected" : "Helpful, not selected" }}
        </span>
      </li>
      <li class="feedback-icon" :class="{ active: isThumbsDown }">
        <i class="las la-thumbs-down feedback-icon-down" aria-hidden="true"></i>
        <span class="feedback-visually-hidden">
          {{ isThumbsDown ? "Not helpful, selected" : "Not helpful, not selected" }}
        </span>
      </li>
    </ul>

    <div
      v-if="showPanel"
      class="feedback-panel"
      role="group"
      :aria-labelledby="panelTitleId"
    >
      <p :id="panelTitleId" class="feedback-panel-title">
        What was wrong with this answer?
      </p>

      <ul class="feedback-reason-list">
        <li
          v-for="reason in reasonOptions"
          :key="reason.id"
          class="feedback-reason"
          :class="{ selected: isReasonSelected(reason.id) }"
        >
          <i
            v-if="isReasonSelected(reason.id)"
            class="las la-check feedback-reason-check"
            aria-hidden="true"
          ></i>
          {{ reason.description }}
          <span class="feedback-visually-hidden">
            {{ isReasonSelected(reason.id) ? ", selected" : ", not selected" }}
          </span>
        </li>
      </ul>

      <dl v-if="hasComment" class="feedback-comment-block">
        <dt class="feedback-comment-label">Tell us more (optional)</dt>
        <dd class="feedback-comment">{{ comment }}</dd>
      </dl>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from "vue";
import EMessageRating from "../../../enums/EMessageRating";

const props = defineProps({
  messageId: { type: String, required: true },
  rating: { type: Number, default: EMessageRating.None },
  // Flags value of the selected reasons; 0 = no reason.
  thumbsDownReason: { type: Number, default: 0 },
  comment: { type: String, default: null },
  // The reason options from the server, as in the chat: { id: flag value, description: label }.
  reasonOptions: { type: Array, required: true }
});

const isThumbsUp = computed(() => props.rating === EMessageRating.ThumbsUp);
const isThumbsDown = computed(() => props.rating === EMessageRating.ThumbsDown);
const hasComment = computed(() => !!props.comment?.trim());

// A thumbs down always shows the panel, also without details (all reasons unselected, no comment). Reasons or a
// comment on another rating cannot be saved, but would still be shown rather than hidden.
const showPanel = computed(
  () =>
    isThumbsDown.value ||
    (props.thumbsDownReason ?? 0) !== 0 ||
    hasComment.value
);

const isReasonSelected = reason =>
  ((props.thumbsDownReason ?? 0) & reason) === reason;

const panelTitleId = computed(() => `feedback-panel-title-${props.messageId}`);
</script>

<style scoped>
/* Mirrors the chat's feedback styles (VindFront Chat.vue, PBI 11215). The not-selected thumb uses #767676 instead
   of the chat's faded disabled button, so the read-only graphic keeps 3:1 against white (WCAG 1.4.11). */
.message-feedback-summary {
  width: 100%;
}

.message-feedback {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
}

.feedback-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  color: #767676;
  font-size: 18px;
  line-height: 1;
}

.feedback-icon.active {
  color: #0a108f;
}

.feedback-icon-down {
  transform: scaleX(-1);
}

.feedback-panel {
  box-sizing: border-box;
  width: 100%;
  max-width: 440px;
  margin-top: 8px;
  padding: 12px 14px;
  border: 1px solid #767676;
  border-radius: 8px;
  background: #fff;
  color: #333;
}

.feedback-panel-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
}

.feedback-reason-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.feedback-reason {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  box-sizing: border-box;
  padding: 4px 12px;
  border: 1px solid #767676;
  border-radius: 14px;
  background: #fff;
  color: #333;
  font-size: 13px;
}

.feedback-reason.selected {
  border-color: #0a108f;
  box-shadow: inset 0 0 0 1px #0a108f;
  background: #eef0fb;
  color: #0a108f;
  font-weight: 600;
}

.feedback-reason-check {
  font-size: 11px;
}

.feedback-comment-block {
  margin: 0;
}

.feedback-comment-label {
  display: block;
  margin: 12px 0 4px;
  font-size: 13px;
  font-weight: normal;
}

.feedback-comment {
  box-sizing: border-box;
  width: 100%;
  min-height: 52px;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid #767676;
  border-radius: 6px;
  color: #333;
  font-size: 13px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.feedback-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
